<!-- Domain management — buy (Cloudflare Registrar) or connect an existing domain.
     All Cloudflare API calls are proxied through the backend /api/domains/* routes.
     Domain state lives here locally; domainStore.js is a stub for future extraction. -->
<script setup>
import { computed, reactive, ref, watch } from 'vue'
import { API_BASE_URL, company, website, getAuthToken } from '@/stores/appConfig'
import { logAction } from '@/utils/logAction'

// Separate from API_BASE_URL — this is the frontend origin used to build the
// public site URL shown to the contractor (e.g. quotastic.ca/site/<company_id>).
const BASE_URL = (() => {
  const { hostname } = window.location
  if (hostname === 'localhost' || hostname === '127.0.0.1') return 'http://localhost:8080'
  return 'https://quotastic.ca'
})()

const quotasticUrl = computed(() =>
  company.company_id ? `${BASE_URL}/site/${company.company_id}` : ''
)

async function authHeaders() {
  const token = await getAuthToken()
  return {
    'Content-Type': 'application/json',
    ...(token && { Authorization: `Bearer ${token}` }),
  }
}

// ── Stepper ───────────────────────────────────────────────────────────────────
const showStepper = ref(false)
const step = ref(1)
const buyMethod = ref(null) // 'buy' | 'connect'
const completedDomain = ref('')
const completedNameservers = ref([])
const completedAction = ref('') // 'registered' | 'connected'

function openStepper() {
  step.value = 1
  buyMethod.value = null
  domainSearch.value = ''
  selectedDomain.value = null
  connectDomain.value = ''
  searchResults.value = []
  searchPerformed.value = false
  actionError.value = ''
  Object.assign(contactForm, {
    first_name: '', last_name: '', email: company.company_email || '',
    phone: '', address: '', city: '', state: '', zip: '', country: 'CA',
  })
  showStepper.value = true
  loadSubscription()
}

function closeStepper() {
  showStepper.value = false
}

function chooseMethod(method) {
  buyMethod.value = method
  step.value = 2
}

// ── Plan / cart step ─────────────────────────────────────────────────────
// No real billing yet (see backend/routes/subscriptions.js) — this records
// agreement to the bundled plan before the actual Cloudflare registration/
// connection call runs, rather than letting "Buy" silently imply a one-time
// domain-only purchase. One subscription per company covers all their
// domains, so a company that already has one just sees lighter copy here.
const subscription = ref(null)
const subscribing = ref(false)

async function loadSubscription() {
  if (!company.company_id) return
  try {
    const headers = await authHeaders()
    const res = await fetch(`${API_BASE_URL}/subscriptions?company_id=${company.company_id}`, { headers })
    const data = await res.json().catch(() => ({}))
    if (res.ok && data.ok) subscription.value = data.subscription
  } catch {
    // Non-critical — worst case the cart step shows "new subscriber" copy
    // for someone who already subscribed; it's still correct, just not the
    // shorter wording.
  }
}

const pendingDomainName = computed(() =>
  buyMethod.value === 'buy' ? selectedDomain.value?.domain || '' : normalizeDomain(connectDomain.value)
)

// Beta: this reserves the domain instead of actually registering/connecting
// it — see backend/routes/domainReservations.js. Real Cloudflare purchase/
// connect happens by hand from the admin side once staff reviews it, not
// automatically from this flow. buyDomain()/connectExistingDomain() below
// still work and are kept for when this is switched back on later; nothing
// currently calls them.
const reservedDomain = ref(null)

async function reserveDomain() {
  const body = {
    company_id: company.company_id,
    domain: pendingDomainName.value,
    action: buyMethod.value === 'buy' ? 'register' : 'connect',
  }

  if (buyMethod.value === 'buy') {
    const { first_name, last_name, email, phone, address, city, state, zip, country } = contactForm
    if (first_name && last_name && email) {
      body.registrant_contact = { first_name, last_name, email, phone, address1: address, city, state, zip, country }
    }
    if (isCaDomain.value) {
      body.contact_extensions = { ca_legal_type: caLegalType.value }
    }
  }

  const headers = await authHeaders()
  const res = await fetch(`${API_BASE_URL}/domain-reservations`, {
    method: 'POST', headers, body: JSON.stringify(body),
  })
  const data = await res.json().catch(() => ({}))
  if (!res.ok || !data.ok) throw new Error(data.error || 'Unable to reserve this domain.')

  reservedDomain.value = data.reservation
  completedDomain.value = pendingDomainName.value
  completedAction.value = 'reserved'
  step.value = 4
  await loadMyReservations()
}

async function confirmPlanAndProceed() {
  subscribing.value = true
  actionError.value = ''
  try {
    if (!subscription.value) {
      const headers = await authHeaders()
      const res = await fetch(`${API_BASE_URL}/subscriptions`, {
        method: 'POST', headers,
        body: JSON.stringify({ company_id: company.company_id }),
      })
      const data = await res.json().catch(() => ({}))
      if (!res.ok || !data.ok) throw new Error(data.error || 'Unable to record your subscription.')
      subscription.value = data.subscription
    }

    await reserveDomain()
  } catch (err) {
    actionError.value = err.message || 'Something went wrong.'
  } finally {
    subscribing.value = false
  }
}

// ── Form state ────────────────────────────────────────────────────────────────
const domainSearch = ref('')
const selectedDomain = ref(null)
const connectDomain = ref('')

const contactForm = reactive({
  first_name: '', last_name: '', email: '',
  phone: '', address: '', city: '', state: '', zip: '', country: 'CA',
})

// .ca domains require CIRA's Canadian-presence declaration on top of the
// generic ICANN contact — Cloudflare rejects the purchase without it (see
// backend/routes/domains.js's contact_extensions comment for the full code
// list and where these came from).
const caLegalType = ref('')
const CA_LEGAL_TYPES = [
  { value: 'CCT', label: 'Canadian Citizen' },
  { value: 'RES', label: 'Permanent Resident of Canada' },
  { value: 'CCO', label: 'Canadian Corporation' },
  { value: 'ASS', label: 'Canadian Unincorporated Association' },
  { value: 'PRT', label: 'Partnership (Canadian-majority)' },
  { value: 'TRD', label: 'Trademark Holder (registered in Canada)' },
  { value: 'TDM', label: 'Trademark Applicant (filed in Canada)' },
  { value: 'TRS', label: 'Trust Established in Canada' },
  { value: 'GOV', label: 'Government (Federal / Provincial / Local)' },
  { value: 'EDU', label: 'Canadian Educational Institution' },
  { value: 'LAM', label: 'Canadian Library, Archive, or Museum' },
  { value: 'MAJ', label: 'Canadian Majority-Owned Subsidiary' },
  { value: 'ABO', label: 'Aboriginal Peoples' },
  { value: 'INB', label: 'Indian Band (Indian Act)' },
  { value: 'LGR', label: 'Legal Representative of a Canadian Citizen/Resident' },
  { value: 'OMK', label: 'Official Mark Holder' },
  { value: 'PLT', label: 'Canadian Political Party' },
  { value: 'HOP', label: 'Her Majesty the Queen' },
]

// ── API state ─────────────────────────────────────────────────────────────────
const isSearching = ref(false)
const searchResults = ref([])
const searchPerformed = ref(false)
const searchError = ref('')

const myDomains = ref([])
const isLoadingDomains = ref(true)

const isActioning = ref(false)
const actionError = ref('')
const actionSuccess = ref('')

const copiedText = ref('')
const copiedUrl = ref(false)

// ── Validation ────────────────────────────────────────────────────────────────
const DOMAIN_RE = /^([a-z0-9]([a-z0-9-]*[a-z0-9])?\.)+[a-z]{2,}$/i

function normalizeDomain(raw) {
  return raw.trim()
    .replace(/^https?:\/\//i, '')
    .replace(/^www\./i, '')
    .replace(/\/.*/, '')
    .toLowerCase()
}

const connectDomainError = computed(() => {
  if (!connectDomain.value) return ''
  return DOMAIN_RE.test(normalizeDomain(connectDomain.value))
    ? '' : 'Enter a valid domain (e.g. tylandscaping.com)'
})

const isCaDomain = computed(() => Boolean(selectedDomain.value?.domain?.toLowerCase().endsWith('.ca')))

// ── Computed ──────────────────────────────────────────────────────────────────
const hasDomains = computed(() => !isLoadingDomains.value && myDomains.value.length > 0)

const domainKeyword = computed(() =>
  domainSearch.value.toLowerCase().trim()
    .replace(/^https?:\/\//, '').replace(/^www\./, '')
    .replace(/[^a-z0-9.-]+/g, '').split('.')[0]
)

const primaryDomain = computed(() => website.domain || null)
const activeDomain = computed(() => myDomains.value.find(d => d.status === 'active') ?? null)

const DNS_TYPES = ['A', 'AAAA', 'CNAME', 'MX', 'TXT', 'NS', 'SRV', 'CAA']
const TTL_OPTIONS = [
  { label: 'Auto', value: 1 },
  { label: '5 min', value: 300 },
  { label: '30 min', value: 1800 },
  { label: '1 hr', value: 3600 },
  { label: '1 day', value: 86400 },
]

// ── Actions ───────────────────────────────────────────────────────────────────
async function searchDomains() {
  const kw = domainKeyword.value || 'yourbusiness'
  isSearching.value = true
  searchError.value = ''
  searchResults.value = []
  searchPerformed.value = true
  const queries = [
    { domain: `${kw}.ca`,         tag: 'Best fit'    },
    { domain: `${kw}.com`,        tag: 'Popular'     },
    { domain: `${kw}services.ca`, tag: 'Alternative' },
  ]
  try {
    const headers = await authHeaders()
    const settled = await Promise.allSettled(
      queries.map(async ({ domain, tag }) => {
        const res = await fetch(`${API_BASE_URL}/domains/check?domain=${encodeURIComponent(domain)}`, { headers })
        if (!res.ok) throw new Error()
        return { ...await res.json(), tag }
      })
    )
    searchResults.value = settled.map((r, i) =>
      r.status === 'fulfilled'
        ? r.value
        : { domain: queries[i].domain, available: null, price: null, tag: queries[i].tag, failed: true }
    )
  } catch {
    searchError.value = 'Search failed. Please try again.'
  } finally {
    isSearching.value = false
  }
}

function selectDomain(result) {
  if (!result.available) return
  selectedDomain.value = result
  caLegalType.value = ''
  if (!contactForm.email) contactForm.email = company.company_email || ''
}

// Not called anywhere right now — reserveDomain()/confirmPlanAndProceed()
// above call /domain-reservations instead for the beta reserve-and-fulfill
// flow (see backend/routes/domainReservations.js). Kept intact, unchanged,
// for when the real purchase flow gets switched back on.
// eslint-disable-next-line no-unused-vars
async function buyDomain() {
  if (!selectedDomain.value) return
  if (isCaDomain.value && !caLegalType.value) {
    actionError.value = 'Select a Canadian presence declaration before registering a .ca domain.'
    return
  }
  isActioning.value = true
  actionError.value = ''
  try {
    const headers = await authHeaders()
    const body = { company_id: company.company_id, domain: selectedDomain.value.domain }
    const { first_name, last_name, email, phone, address, city, state, zip, country } = contactForm
    if (first_name && last_name && email) {
      body.registrant_contact = { first_name, last_name, email, phone, address1: address, city, state, zip, country }
    }
    if (isCaDomain.value) {
      body.contact_extensions = { ca_legal_type: caLegalType.value }
    }
    const res = await fetch(`${API_BASE_URL}/domains/register`, {
      method: 'POST', headers, body: JSON.stringify(body),
    })
    const data = await res.json()
    if (!res.ok) throw new Error(data.detail || data.error || 'Registration failed')
    logAction(headers.Authorization?.slice(7), {
      action: 'domain.register',
      label: `Registered domain ${selectedDomain.value.domain}`,
      company_id: company.company_id,
    })
    completedDomain.value = selectedDomain.value.domain
    completedNameservers.value = []
    completedAction.value = 'registered'
    step.value = 4
    await loadMyDomains()
  } catch (err) {
    actionError.value = err.message
  } finally {
    isActioning.value = false
  }
}

// Same as buyDomain() above — intentionally unused right now, kept for later.
// eslint-disable-next-line no-unused-vars
async function connectExistingDomain() {
  const domain = normalizeDomain(connectDomain.value)
  if (!domain || !DOMAIN_RE.test(domain)) { actionError.value = 'Enter a valid domain name.'; return }
  isActioning.value = true
  actionError.value = ''
  try {
    const headers = await authHeaders()
    const res = await fetch(`${API_BASE_URL}/domains/connect`, {
      method: 'POST', headers,
      body: JSON.stringify({ company_id: company.company_id, domain }),
    })
    const data = await res.json()
    if (!res.ok) throw new Error(data.error || 'Connection failed')
    logAction(headers.Authorization?.slice(7), {
      action: 'domain.connect',
      label: `Connected domain ${domain}`,
      company_id: company.company_id,
    })
    completedDomain.value = domain
    completedNameservers.value = data.nameservers ?? []
    completedAction.value = 'connected'
    step.value = 4
    await loadMyDomains()
  } catch (err) {
    actionError.value = err.message
  } finally {
    isActioning.value = false
  }
}

async function loadMyDomains() {
  if (!company.company_id) { isLoadingDomains.value = false; return }
  isLoadingDomains.value = true
  try {
    const headers = await authHeaders()
    const res = await fetch(`${API_BASE_URL}/domains?company_id=${company.company_id}`, { headers })
    const data = await res.json().catch(() => ({}))
    if (!res.ok) throw new Error(data.error || `Unable to load domains (${res.status}).`)
    myDomains.value = data.domains ?? []
    // Auto-populate nameservers for any pending domain so users don't have to click
    const pending = myDomains.value.filter(d => d.status === 'pending')
    if (pending.length) await Promise.allSettled(pending.map(d => checkStatus(d)))
  } catch (err) {
    actionError.value = err.message || 'Unable to load domains.'
  } finally {
    isLoadingDomains.value = false
  }
}

// Reservations made through the beta reserve-and-fulfill flow (see
// reserveDomain() above) that staff hasn't fulfilled yet — surfaced
// separately from myDomains since they have no contractor_domains row until
// fulfilled, so without this a contractor who just reserved would see
// nothing at all and assume it didn't work.
const myReservations = ref([])
const pendingReservations = computed(() => myReservations.value.filter(r => r.status === 'pending'))

async function loadMyReservations() {
  if (!company.company_id) return
  try {
    const headers = await authHeaders()
    const res = await fetch(`${API_BASE_URL}/domain-reservations?company_id=${company.company_id}`, { headers })
    const data = await res.json().catch(() => ({}))
    if (res.ok && data.ok) myReservations.value = data.reservations ?? []
  } catch {
    // Non-critical — worst case a pending reservation just doesn't show
    // until the page is reloaded.
  }
}

async function checkStatus(domain) {
  domain._checking = true
  try {
    const headers = await authHeaders()
    const res = await fetch(`${API_BASE_URL}/domains/${domain.id}/status`, { headers })
    const data = await res.json()
    domain.status = data.status
    domain._nameservers = data.nameservers
  } catch (err) {
    console.error('checkStatus error:', err)
  } finally {
    domain._checking = false
  }
}

async function removeDomain(domain) {
  if (!confirm(`Remove ${domain.domain} from your account?`)) return
  try {
    const headers = await authHeaders()
    const res = await fetch(`${API_BASE_URL}/domains/${domain.id}`, { method: 'DELETE', headers })
    if (res.ok) {
      logAction(headers.Authorization?.slice(7), {
        action: 'domain.delete',
        label: `Removed domain ${domain.domain}`,
        company_id: company.company_id,
      })
    }
    myDomains.value = myDomains.value.filter(d => d.id !== domain.id)
    if (website.domain === domain.domain) website.domain = null
  } catch (err) {
    console.error('removeDomain error:', err)
  }
}

async function setPrimaryDomain(domain) {
  isActioning.value = true
  actionError.value = ''
  actionSuccess.value = ''
  try {
    const headers = await authHeaders()
    const res = await fetch(`${API_BASE_URL}/domains/${domain.id}/set-primary`, {
      method: 'PATCH', headers,
    })
    const data = await res.json()
    if (!res.ok) throw new Error(data.error || 'Failed to set primary domain')
    logAction(headers.Authorization?.slice(7), {
      action: 'domain.set-primary',
      label: `Set ${domain.domain} as primary domain`,
      company_id: company.company_id,
    })
    website.domain = domain.domain
    actionSuccess.value = `${domain.domain} is now your primary domain.`
    setTimeout(() => { actionSuccess.value = '' }, 3000)
  } catch (err) {
    actionError.value = err.message
  } finally {
    isActioning.value = false
  }
}

async function saveEmail() {
  if (!company.owner_firebase_uid) return
  try {
    const headers = await authHeaders()
    await fetch(`${API_BASE_URL}/companies/upsert`, {
      method: 'POST', headers,
      body: JSON.stringify({
        owner_firebase_uid: company.owner_firebase_uid,
        company_email: company.company_email,
      }),
    })
  } catch (err) {
    console.error('saveEmail error:', err)
  }
}

// ── DNS record management ─────────────────────────────────────────────────────

async function toggleDns(domain) {
  domain._dnsOpen = !domain._dnsOpen
  if (domain._dnsOpen && domain._dnsRecords === undefined) {
    await loadDnsRecords(domain)
  }
}

async function loadDnsRecords(domain) {
  domain._dnsLoading = true
  domain._dnsError = ''
  try {
    const headers = await authHeaders()
    const res = await fetch(`${API_BASE_URL}/domains/${domain.id}/dns-records`, { headers })
    const data = await res.json()
    if (!res.ok) throw new Error(data.error || 'Failed to load DNS records')
    domain._dnsRecords = data.records
  } catch (err) {
    domain._dnsError = err.message
  } finally {
    domain._dnsLoading = false
  }
}

function startAddRecord(domain) {
  domain._addingRecord = true
  domain._addForm = { type: 'A', name: '', content: '', ttl: 1, proxied: false }
}

async function addDnsRecord(domain) {
  if (!domain._addForm?.content) return
  domain._dnsSaving = true
  domain._dnsError = ''
  try {
    const headers = await authHeaders()
    const res = await fetch(`${API_BASE_URL}/domains/${domain.id}/dns-records`, {
      method: 'POST', headers,
      body: JSON.stringify(domain._addForm),
    })
    const data = await res.json()
    if (!res.ok) throw new Error(data.error || 'Failed to add record')
    logAction(headers.Authorization?.slice(7), {
      action: 'dns.create',
      label: `Added ${domain._addForm.type} record: ${domain._addForm.name}`,
      company_id: company.company_id,
      metadata: { domain: domain.domain, type: domain._addForm.type, name: domain._addForm.name },
    })
    domain._dnsRecords = [...(domain._dnsRecords ?? []), data.record]
    domain._addingRecord = false
  } catch (err) {
    domain._dnsError = err.message
  } finally {
    domain._dnsSaving = false
  }
}

function startEditRecord(domain, record) {
  domain._editingRecord = record.id
  domain._editForm = {
    type: record.type, name: record.name,
    content: record.content, ttl: record.ttl, proxied: record.proxied,
  }
}

function cancelEdit(domain) {
  domain._editingRecord = null
}

async function saveEditRecord(domain) {
  if (!domain._editingRecord) return
  domain._dnsSaving = true
  domain._dnsError = ''
  try {
    const headers = await authHeaders()
    const res = await fetch(`${API_BASE_URL}/domains/${domain.id}/dns-records/${domain._editingRecord}`, {
      method: 'PATCH', headers,
      body: JSON.stringify(domain._editForm),
    })
    const data = await res.json()
    if (!res.ok) throw new Error(data.error || 'Failed to update record')
    logAction(headers.Authorization?.slice(7), {
      action: 'dns.update',
      label: `Updated ${domain._editForm.type} record: ${domain._editForm.name}`,
      company_id: company.company_id,
      metadata: { domain: domain.domain, type: domain._editForm.type, name: domain._editForm.name },
    })
    const idx = domain._dnsRecords.findIndex(r => r.id === domain._editingRecord)
    if (idx !== -1) domain._dnsRecords[idx] = data.record
    domain._editingRecord = null
  } catch (err) {
    domain._dnsError = err.message
  } finally {
    domain._dnsSaving = false
  }
}

async function deleteDnsRecord(domain, recordId) {
  if (!confirm('Delete this DNS record?')) return
  domain._dnsSaving = true
  domain._dnsError = ''
  try {
    const headers = await authHeaders()
    const res = await fetch(`${API_BASE_URL}/domains/${domain.id}/dns-records/${recordId}`, {
      method: 'DELETE', headers,
    })
    const data = await res.json()
    if (!res.ok) throw new Error(data.error || 'Failed to delete record')
    const deleted = domain._dnsRecords?.find(r => r.id === recordId)
    logAction(headers.Authorization?.slice(7), {
      action: 'dns.delete',
      label: `Deleted ${deleted?.type ?? 'DNS'} record: ${deleted?.name ?? recordId}`,
      company_id: company.company_id,
      metadata: { domain: domain.domain },
    })
    domain._dnsRecords = domain._dnsRecords.filter(r => r.id !== recordId)
  } catch (err) {
    domain._dnsError = err.message
  } finally {
    domain._dnsSaving = false
  }
}

async function copyText(text) {
  try {
    await navigator.clipboard.writeText(text)
    copiedText.value = text
    setTimeout(() => { copiedText.value = '' }, 1800)
  } catch (err) {
    console.error('copyText error:', err)
  }
}

async function copyQuotasticUrl() {
  try {
    await navigator.clipboard.writeText(quotasticUrl.value)
    copiedUrl.value = true
    setTimeout(() => { copiedUrl.value = false }, 1800)
  } catch (err) {
    console.error('copyQuotasticUrl error:', err)
  }
}

function previewSite() {
  if (quotasticUrl.value) window.open(quotasticUrl.value, '_blank', 'noopener')
}

function clearFeedback() {
  actionError.value = ''
  actionSuccess.value = ''
}

watch(() => company.company_id, () => { loadMyDomains(); loadMyReservations() }, { immediate: true })
</script>

<template>
  <main class="domain-page">
    <section class="domain-shell">

      <!-- ── Loading ──────────────────────────────────────────────────────── -->
      <template v-if="isLoadingDomains">
        <div class="loading-header">
          <div class="skel skel-eyebrow" />
          <div class="skel skel-h1" />
        </div>
        <div class="skel skel-hero" />
        <div class="skel skel-card" style="height:160px;margin-top:12px" />
      </template>

      <!-- ── Stepper view ──────────────────────────────────────────────────── -->
      <template v-else-if="showStepper">
        <header class="page-header">
          <div>
            <p class="eyebrow">CMS</p>
            <h1>{{ hasDomains ? 'Add a domain' : 'Domain setup' }}</h1>
          </div>
          <button class="btn-ghost" type="button" @click="closeStepper">
            <v-icon size="18">mdi-close</v-icon>
            <span>Cancel</span>
          </button>
        </header>

        <div class="stepper-wrap">

          <!-- Step progress -->
          <div class="step-progress">
            <div v-for="n in 4" :key="n" class="step-progress-item">
              <div class="step-dot" :class="{ current: step === n, done: step > n }">
                <v-icon v-if="step > n" size="13">mdi-check</v-icon>
                <span v-else>{{ n }}</span>
              </div>
              <div v-if="n < 4" class="step-connector" :class="{ done: step > n }" />
            </div>
          </div>

          <!-- Error bar -->
          <transition name="fade">
            <div v-if="actionError" class="feedback-bar error">
              <v-icon size="17">mdi-alert-circle-outline</v-icon>
              <span>{{ actionError }}</span>
              <button class="feedback-close" type="button" @click="clearFeedback">
                <v-icon size="16">mdi-close</v-icon>
              </button>
            </div>
          </transition>

          <!-- Step 1: Choose method -->
          <div v-if="step === 1" class="step-body">
            <h2 class="step-heading">How do you want to connect your domain?</h2>
            <p class="step-sub">Your site is already live at your free Quotastic URL. A custom domain makes it more memorable.</p>
            <p class="step-sub">Nothing is purchased or connected until you confirm on the last step.</p>

            <div class="method-list">
              <button class="method-card" type="button" @click="chooseMethod('buy')">
                <div class="method-icon">
                  <v-icon size="22">mdi-cart-outline</v-icon>
                </div>
                <div class="method-text">
                  <strong>Buy a domain</strong>
                  <span>Register a new domain through Quotastic from ~$15/yr</span>
                </div>
                <v-icon size="18" class="method-arrow">mdi-arrow-right</v-icon>
              </button>
              <button class="method-card" type="button" @click="chooseMethod('connect')">
                <div class="method-icon">
                  <v-icon size="22">mdi-link-plus</v-icon>
                </div>
                <div class="method-text">
                  <strong>Connect existing domain</strong>
                  <span>Point a domain you already own to your Quotastic site</span>
                </div>
                <v-icon size="18" class="method-arrow">mdi-arrow-right</v-icon>
              </button>
            </div>
          </div>

          <!-- Step 2: Buy -->
          <div v-else-if="step === 2 && buyMethod === 'buy'" class="step-body">
            <h2 class="step-heading">Find your domain</h2>
            <p class="step-sub">Searching and selecting a domain here doesn't register it yet — you'll confirm on the last step.</p>

            <label class="field">
              <span>Search</span>
              <div class="search-input">
                <v-icon size="19">mdi-magnify</v-icon>
                <input
                  v-model="domainSearch"
                  type="search"
                  placeholder="tylandscaping"
                  autocomplete="off"
                  spellcheck="false"
                  @keydown.enter="searchDomains"
                />
                <button type="button" :disabled="isSearching" @click="searchDomains">
                  <v-icon size="16" :class="{ spin: isSearching }">
                    {{ isSearching ? 'mdi-loading' : 'mdi-arrow-right' }}
                  </v-icon>
                </button>
              </div>
            </label>

            <p v-if="searchError" class="inline-error">{{ searchError }}</p>

            <div v-if="isSearching" class="domain-results">
              <div v-for="n in 3" :key="n" class="skel" style="height:58px;border-radius:8px" />
            </div>
            <div v-else-if="searchPerformed" class="domain-results">
              <button
                v-for="result in searchResults"
                :key="result.domain"
                class="domain-result-row"
                :class="{
                  selected: selectedDomain?.domain === result.domain,
                  unavailable: result.available === false || result.failed,
                }"
                type="button"
                :disabled="result.available === false || result.failed"
                @click="selectDomain(result)"
              >
                <span>
                  <strong>{{ result.domain }}</strong>
                  <small>{{ result.available === false ? 'Unavailable' : result.failed ? 'Could not check' : result.tag }}</small>
                </span>
                <span v-if="result.available !== false && !result.failed" class="domain-price">
                  {{ result.price ? result.price + '/yr' : '—' }}
                </span>
                <span v-else class="domain-taken">Taken</span>
              </button>
            </div>

            <!-- Registrant contact (slides in when domain selected) -->
            <transition name="slide-down">
              <div v-if="selectedDomain" class="contact-section">
                <p class="contact-title">Registration contact <span>Required by ICANN</span></p>
                <div class="contact-grid">
                  <label class="field">
                    <span>First name</span>
                    <input v-model="contactForm.first_name" type="text" autocomplete="given-name" />
                  </label>
                  <label class="field">
                    <span>Last name</span>
                    <input v-model="contactForm.last_name" type="text" autocomplete="family-name" />
                  </label>
                  <label class="field">
                    <span>Email</span>
                    <input v-model="contactForm.email" type="email" autocomplete="email" />
                  </label>
                  <label class="field">
                    <span>Phone</span>
                    <input v-model="contactForm.phone" type="tel" placeholder="+1 416 555 0100" autocomplete="tel" />
                  </label>
                  <label class="field contact-full">
                    <span>Street address</span>
                    <input v-model="contactForm.address" type="text" autocomplete="street-address" />
                  </label>
                  <label class="field">
                    <span>City</span>
                    <input v-model="contactForm.city" type="text" autocomplete="address-level2" />
                  </label>
                  <label class="field">
                    <span>Province / State</span>
                    <input v-model="contactForm.state" type="text" autocomplete="address-level1" />
                  </label>
                  <label class="field">
                    <span>Postal code</span>
                    <input v-model="contactForm.zip" type="text" autocomplete="postal-code" />
                  </label>
                  <label class="field">
                    <span>Country</span>
                    <input v-model="contactForm.country" type="text" autocomplete="country" />
                  </label>
                </div>

                <div v-if="isCaDomain" class="ca-legal-section">
                  <p class="contact-title">Canadian presence declaration <span>Required by CIRA for .ca domains</span></p>
                  <label class="field contact-full">
                    <span>Registrant type</span>
                    <select v-model="caLegalType">
                      <option value="" disabled>Select the option that applies to the registrant</option>
                      <option v-for="type in CA_LEGAL_TYPES" :key="type.value" :value="type.value">
                        {{ type.label }}
                      </option>
                    </select>
                  </label>
                </div>
              </div>
            </transition>

            <div class="step-nav">
              <button class="btn-ghost" type="button" @click="step = 1">
                <v-icon size="17">mdi-arrow-left</v-icon>
                Back
              </button>
              <button
                class="btn-primary"
                type="button"
                :disabled="!selectedDomain || (isCaDomain && !caLegalType)"
                @click="step = 3"
              >
                <span>Continue</span>
              </button>
            </div>
          </div>

          <!-- Step 2: Connect -->
          <div v-else-if="step === 2 && buyMethod === 'connect'" class="step-body">
            <h2 class="step-heading">Enter your domain</h2>
            <p class="step-sub">We'll add it to Cloudflare and give you nameservers to set at your registrar. Entering it here doesn't connect it yet — you'll confirm on the last step.</p>

            <label class="field">
              <span>Domain name</span>
              <input
                v-model="connectDomain"
                type="text"
                placeholder="tylandscaping.com"
                autocomplete="off"
                spellcheck="false"
              />
            </label>
            <p v-if="connectDomainError" class="inline-error">{{ connectDomainError }}</p>

            <div class="step-nav">
              <button class="btn-ghost" type="button" @click="step = 1">
                <v-icon size="17">mdi-arrow-left</v-icon>
                Back
              </button>
              <button
                class="btn-primary"
                type="button"
                :disabled="!connectDomain || !!connectDomainError"
                @click="step = 3"
              >
                <span>Continue</span>
              </button>
            </div>
          </div>

          <!-- Step 3: Plan / cart -->
          <div v-else-if="step === 3" class="step-body">
            <h2 class="step-heading">Confirm your plan</h2>
            <p class="step-sub">
              This is the final step — clicking Reserve domain below will reserve <strong>{{ pendingDomainName }}</strong>
              and subscribe your account. Nothing has happened yet.
            </p>

            <div class="plan-card">
              <div class="plan-card-domain">
                <v-icon size="18">mdi-earth</v-icon>
                <strong>{{ pendingDomainName }}</strong>
              </div>
              <div class="plan-card-price">
                <span class="plan-price">$15 CAD</span>
                <span class="plan-period">/month</span>
              </div>
              <ul class="plan-features">
                <li><v-icon size="15">mdi-check</v-icon> CRM &amp; lead capture</li>
                <li><v-icon size="15">mdi-check</v-icon> AI website builder</li>
                <li><v-icon size="15">mdi-check</v-icon> This domain</li>
                <li><v-icon size="15">mdi-check</v-icon> Hosting</li>
              </ul>
            </div>

            <p v-if="subscription" class="step-sub">
              This domain will be added to your existing $15 CAD/month plan — you're already subscribed, so
              nothing further is charged separately for it.
            </p>
            <p v-else class="step-sub">
              By reserving, you're subscribing your account to the plan above. No payment is collected here yet —
              billing setup is coming soon, and this locks in your $15 CAD/month founding price ahead of that.
            </p>
            <p class="step-sub">
              Domain registration is completed by our team, not instantly — it can take up to an hour.
            </p>

            <p v-if="actionError" class="inline-error">{{ actionError }}</p>

            <div class="step-nav">
              <button class="btn-ghost" type="button" @click="step = 2">
                <v-icon size="17">mdi-arrow-left</v-icon>
                Back
              </button>
              <button
                class="btn-primary"
                type="button"
                :disabled="subscribing || isActioning"
                @click="confirmPlanAndProceed"
              >
                <v-icon v-if="subscribing || isActioning" size="17" class="spin">mdi-loading</v-icon>
                <span>{{ subscribing || isActioning ? 'Working…' : 'Reserve domain' }}</span>
              </button>
            </div>
          </div>

          <!-- Step 4: Done -->
          <div v-else-if="step === 4" class="step-body step-done">
            <template v-if="completedAction === 'reserved'">
              <div class="done-icon done-icon--pending">
                <v-icon size="28">mdi-clock-outline</v-icon>
              </div>
              <h2 class="step-heading">{{ completedDomain }} reserved</h2>
              <p class="step-sub">
                You're subscribed, and our team is completing registration for {{ completedDomain }} now —
                this can take up to an hour. It'll show up in your domains list once it's live.
              </p>
            </template>
            <template v-else-if="completedAction === 'registered'">
              <div class="done-icon done-icon--success">
                <v-icon size="28">mdi-check</v-icon>
              </div>
              <h2 class="step-heading">{{ completedDomain }} registered</h2>
              <p class="step-sub">Your domain is yours. DNS changes may take up to 48 hours to propagate globally.</p>
            </template>
            <template v-else>
              <div class="done-icon done-icon--pending">
                <v-icon size="28">mdi-clock-outline</v-icon>
              </div>
              <h2 class="step-heading">Update your nameservers</h2>
              <p class="step-sub">
                At your domain registrar, set the nameservers for <strong>{{ completedDomain }}</strong> to:
              </p>
              <div v-if="completedNameservers.length" class="ns-block">
                <div v-for="ns in completedNameservers" :key="ns" class="ns-row">
                  <code>{{ ns }}</code>
                  <button class="copy-btn" type="button" :class="{ copied: copiedText === ns }" @click="copyText(ns)">
                    <v-icon size="13">{{ copiedText === ns ? 'mdi-check' : 'mdi-content-copy' }}</v-icon>
                  </button>
                </div>
              </div>
              <p class="step-hint">DNS propagation can take up to 48 hours. Check status from your domains list.</p>
            </template>

            <button class="btn-primary" type="button" style="margin-top:8px" @click="closeStepper">
              View my domains
            </button>
          </div>

        </div>
      </template>

      <!-- ── Setup hero: no domains, stepper closed ────────────────────────── -->
      <template v-else-if="!hasDomains">
        <header class="page-header">
          <div>
            <p class="eyebrow">CMS</p>
            <h1>Domain setup</h1>
          </div>
          <button class="btn-ghost" type="button" @click="previewSite">
            <v-icon size="18">mdi-eye-outline</v-icon>
            <span>Preview site</span>
          </button>
        </header>

        <transition name="fade">
          <div v-if="actionError" class="feedback-bar error">
            <v-icon size="17">mdi-alert-circle-outline</v-icon>
            <span>{{ actionError }}</span>
            <button class="feedback-close" type="button" @click="clearFeedback">
              <v-icon size="16">mdi-close</v-icon>
            </button>
          </div>
        </transition>

        <div v-if="pendingReservations.length" class="reservation-notice">
          <v-icon size="17">mdi-clock-outline</v-icon>
          <span>
            <strong>{{ pendingReservations[0].domain }}</strong> is reserved — our team is completing registration
            now, up to an hour. It'll appear here once it's live.
          </span>
        </div>

        <div class="hero-grid">
          <!-- Free URL card -->
          <div class="url-card">
            <div class="url-card-label">
              <v-icon size="15">mdi-link-variant</v-icon>
              Your free Quotastic URL
            </div>
            <div class="url-card-value">
              <span class="url-text">{{ quotasticUrl }}</span>
              <button class="copy-btn" type="button" :class="{ copied: copiedUrl }" @click="copyQuotasticUrl">
                <v-icon size="13">{{ copiedUrl ? 'mdi-check' : 'mdi-content-copy' }}</v-icon>
              </button>
              <button class="icon-link-btn" type="button" @click="previewSite">
                <v-icon size="14">mdi-open-in-new</v-icon>
              </button>
            </div>
            <p class="url-card-hint">This URL is always active — share it with clients even without a custom domain.</p>
          </div>

          <!-- Custom domain invite -->
          <div class="invite-card">
            <div class="invite-icon">
              <v-icon size="22">mdi-earth</v-icon>
            </div>
            <div class="invite-body">
              <strong>Connect a custom domain</strong>
              <p>Use your own domain name to make your site look more professional and easier to remember.</p>
            </div>
            <button class="btn-primary invite-cta" type="button" @click="openStepper">
              Get started
              <v-icon size="17">mdi-arrow-right</v-icon>
            </button>
          </div>
        </div>
      </template>

      <!-- ── Domains view ──────────────────────────────────────────────────── -->
      <template v-else>
        <header class="page-header">
          <div>
            <p class="eyebrow">CMS</p>
            <h1>Your domains</h1>
          </div>
          <div class="header-actions">
            <button class="btn-ghost" type="button" @click="previewSite">
              <v-icon size="18">mdi-eye-outline</v-icon>
              <span>Preview site</span>
            </button>
            <button class="btn-primary" type="button" @click="openStepper">
              <v-icon size="18">mdi-plus</v-icon>
              <span>Add domain</span>
            </button>
          </div>
        </header>

        <transition name="fade">
          <div v-if="actionSuccess || actionError" class="feedback-bar" :class="{ error: actionError }">
            <v-icon size="17">{{ actionError ? 'mdi-alert-circle-outline' : 'mdi-check-circle-outline' }}</v-icon>
            <span>{{ actionError || actionSuccess }}</span>
            <button class="feedback-close" type="button" @click="clearFeedback">
              <v-icon size="16">mdi-close</v-icon>
            </button>
          </div>
        </transition>

        <!-- Info strip -->
        <div class="info-strip">
          <div class="info-block">
            <span class="label-text">Free URL</span>
            <div class="url-row">
              <span class="url-text-sm">{{ quotasticUrl }}</span>
              <button class="copy-btn" type="button" :class="{ copied: copiedUrl }" @click="copyQuotasticUrl">
                <v-icon size="13">{{ copiedUrl ? 'mdi-check' : 'mdi-content-copy' }}</v-icon>
              </button>
            </div>
          </div>
          <div class="info-block">
            <span class="label-text">Primary domain</span>
            <div v-if="primaryDomain" class="url-chip active">
              <v-icon size="14">mdi-earth</v-icon>
              <span>{{ primaryDomain }}</span>
              <em class="badge active">Primary</em>
            </div>
            <div v-else-if="activeDomain" class="url-chip">
              <v-icon size="14">mdi-earth</v-icon>
              <span>{{ activeDomain.domain }}</span>
              <em class="badge pending">Set primary ↓</em>
            </div>
            <span v-else class="pub-empty">None active yet</span>
          </div>
          <div class="info-block">
            <span class="label-text">Contact email</span>
            <input
              v-model="company.company_email"
              type="email"
              class="inline-input"
              placeholder="you@company.com"
              aria-label="Contact email"
              @blur="saveEmail"
            />
          </div>
          <div class="info-block">
            <span class="label-text">Site status</span>
            <em class="badge pending">Draft</em>
          </div>
        </div>

        <div v-if="pendingReservations.length" class="reservation-notice">
          <v-icon size="17">mdi-clock-outline</v-icon>
          <span>
            <strong>{{ pendingReservations[0].domain }}</strong> is reserved — our team is completing registration
            now, up to an hour.
          </span>
        </div>

        <!-- Domain cards -->
        <div class="domains-list">
          <div v-for="domain in myDomains" :key="domain.id" class="domain-card">

            <div class="domain-card-header">
              <div class="domain-card-title">
                <span class="domain-name">{{ domain.domain }}</span>
                <em class="badge" :class="domain.status">{{ domain.status }}</em>
                <span v-if="domain.domain === primaryDomain" class="primary-label">
                  <v-icon size="13">mdi-star</v-icon>
                  Primary
                </span>
              </div>
              <div class="domain-card-meta">
                <span v-if="domain.expires_at" class="expiry-text">
                  Expires {{ new Date(domain.expires_at).toLocaleDateString('en-CA', { year: 'numeric', month: 'short', day: 'numeric' }) }}
                </span>
                <button
                  v-if="domain.status === 'active' && domain.domain !== primaryDomain"
                  class="btn-text"
                  type="button"
                  :disabled="isActioning"
                  @click="setPrimaryDomain(domain)"
                >
                  <v-icon size="14">mdi-star-outline</v-icon>
                  <span>Set as primary</span>
                </button>
                <button
                  v-if="domain.status === 'pending'"
                  class="btn-text"
                  type="button"
                  :disabled="domain._checking"
                  @click="checkStatus(domain)"
                >
                  <v-icon size="14" :class="{ spin: domain._checking }">
                    {{ domain._checking ? 'mdi-loading' : 'mdi-refresh' }}
                  </v-icon>
                  <span>{{ domain._checking ? 'Checking…' : 'Check DNS' }}</span>
                </button>
                <button class="btn-text danger" type="button" @click="removeDomain(domain)">
                  <v-icon size="14">mdi-delete-outline</v-icon>
                  <span>Remove</span>
                </button>
              </div>
            </div>

            <!-- Nameservers (pending only, auto-loaded on page open) -->
            <div v-if="domain.status === 'pending'" class="ns-block">
              <p class="label-text" style="margin-bottom:8px">
                Point your nameservers to Cloudflare to complete setup:
              </p>
              <div v-if="domain._checking" class="ns-loading">
                <v-icon size="14" class="spin">mdi-loading</v-icon>
                Loading nameservers…
              </div>
              <div v-else-if="domain._nameservers?.length" class="ns-list">
                <div v-for="ns in domain._nameservers" :key="ns" class="ns-row">
                  <code>{{ ns }}</code>
                  <button class="copy-btn" type="button" :class="{ copied: copiedText === ns }" @click="copyText(ns)">
                    <v-icon size="13">{{ copiedText === ns ? 'mdi-check' : 'mdi-content-copy' }}</v-icon>
                  </button>
                </div>
              </div>
              <p v-else class="ns-hint">
                Nameservers unavailable — check your
                <a href="https://dash.cloudflare.com" target="_blank" rel="noopener">Cloudflare dashboard</a>.
              </p>
            </div>

            <!-- DNS record management -->
            <div class="dns-section">
              <button class="dns-toggle" type="button" @click="toggleDns(domain)">
                <span>DNS Records</span>
                <span v-if="domain._dnsRecords" class="dns-count">{{ domain._dnsRecords.length }}</span>
                <v-icon size="16" class="toggle-arrow" :class="{ open: domain._dnsOpen }">mdi-chevron-down</v-icon>
              </button>

              <transition name="slide-down">
                <div v-if="domain._dnsOpen" class="dns-manage">

                  <div v-if="domain._dnsLoading" class="dns-state-row">
                    <v-icon size="15" class="spin">mdi-loading</v-icon>
                    Loading records…
                  </div>

                  <p v-else-if="domain._dnsError" class="inline-error" style="padding:10px 12px">{{ domain._dnsError }}</p>

                  <template v-else-if="domain._dnsRecords">
                    <!-- Records table -->
                    <div class="dns-manage-table">
                      <div class="dns-manage-head">
                        <span>Type</span>
                        <span>Name</span>
                        <span>Content</span>
                        <span>TTL</span>
                        <span>Proxy</span>
                        <span></span>
                      </div>

                      <div v-if="!domain._dnsRecords.length" class="dns-state-row muted">
                        No records yet.
                      </div>

                      <template v-for="record in domain._dnsRecords" :key="record.id">
                        <!-- Edit row -->
                        <div v-if="domain._editingRecord === record.id" class="dns-edit-row">
                          <select v-model="domain._editForm.type" class="dns-select">
                            <option v-for="t in DNS_TYPES" :key="t">{{ t }}</option>
                          </select>
                          <input v-model="domain._editForm.name" class="dns-input" placeholder="@" />
                          <input v-model="domain._editForm.content" class="dns-input" placeholder="Content" />
                          <select v-model="domain._editForm.ttl" class="dns-select">
                            <option v-for="opt in TTL_OPTIONS" :key="opt.value" :value="opt.value">{{ opt.label }}</option>
                          </select>
                          <label class="proxied-toggle">
                            <input type="checkbox" v-model="domain._editForm.proxied" />
                          </label>
                          <div class="dns-row-actions">
                            <button class="btn-icon-sm" type="button" :disabled="domain._dnsSaving" @click="saveEditRecord(domain)">
                              <v-icon size="13">{{ domain._dnsSaving ? 'mdi-loading' : 'mdi-check' }}</v-icon>
                            </button>
                            <button class="btn-icon-sm" type="button" @click="cancelEdit(domain)">
                              <v-icon size="13">mdi-close</v-icon>
                            </button>
                          </div>
                        </div>

                        <!-- View row -->
                        <div v-else class="dns-manage-row">
                          <span class="dns-type-chip">{{ record.type }}</span>
                          <span class="dns-cell ellipsis">{{ record.name }}</span>
                          <span class="dns-cell ellipsis">{{ record.content }}</span>
                          <span class="dns-cell muted">{{ record.ttl === 1 ? 'Auto' : record.ttl + 's' }}</span>
                          <span class="dns-cell">
                            <v-icon size="14" :color="record.proxied ? '#f26e2e' : 'var(--text-secondary)'">
                              mdi-shield-check
                            </v-icon>
                          </span>
                          <div class="dns-row-actions">
                            <button class="btn-icon-sm" type="button" @click="startEditRecord(domain, record)">
                              <v-icon size="13">mdi-pencil-outline</v-icon>
                            </button>
                            <button class="btn-icon-sm danger" type="button" :disabled="domain._dnsSaving" @click="deleteDnsRecord(domain, record.id)">
                              <v-icon size="13">mdi-delete-outline</v-icon>
                            </button>
                          </div>
                        </div>
                      </template>
                    </div>

                    <!-- Add record form -->
                    <transition name="slide-down">
                      <div v-if="domain._addingRecord" class="dns-add-form">
                        <p class="dns-add-title">New record</p>
                        <div class="dns-add-grid">
                          <label class="field">
                            <span>Type</span>
                            <select v-model="domain._addForm.type" class="dns-select full">
                              <option v-for="t in DNS_TYPES" :key="t">{{ t }}</option>
                            </select>
                          </label>
                          <label class="field">
                            <span>Name</span>
                            <input v-model="domain._addForm.name" class="dns-input full" placeholder="@ or subdomain" />
                          </label>
                          <label class="field dns-add-content">
                            <span>Content</span>
                            <input v-model="domain._addForm.content" class="dns-input full" placeholder="IP, hostname, or value" />
                          </label>
                          <label class="field">
                            <span>TTL</span>
                            <select v-model="domain._addForm.ttl" class="dns-select full">
                              <option v-for="opt in TTL_OPTIONS" :key="opt.value" :value="opt.value">{{ opt.label }}</option>
                            </select>
                          </label>
                          <label class="field proxied-field">
                            <span>Proxied</span>
                            <div class="proxied-toggle-wrap">
                              <input type="checkbox" v-model="domain._addForm.proxied" />
                            </div>
                          </label>
                        </div>
                        <p v-if="domain._dnsError" class="inline-error">{{ domain._dnsError }}</p>
                        <div class="dns-add-actions">
                          <button class="btn-ghost" type="button" @click="domain._addingRecord = false">Cancel</button>
                          <button
                            class="btn-primary"
                            type="button"
                            :disabled="!domain._addForm?.content || domain._dnsSaving"
                            @click="addDnsRecord(domain)"
                          >
                            <v-icon v-if="domain._dnsSaving" size="15" class="spin">mdi-loading</v-icon>
                            Add record
                          </button>
                        </div>
                      </div>
                    </transition>

                    <button v-if="!domain._addingRecord" class="dns-add-btn" type="button" @click="startAddRecord(domain)">
                      <v-icon size="15">mdi-plus</v-icon>
                      Add record
                    </button>
                  </template>

                </div>
              </transition>
            </div>

          </div>
        </div>
      </template>

    </section>
  </main>
</template>

<style scoped>
.domain-page {
  min-height: calc(100dvh - 70px);
  padding: 28px 18px 104px;
  background: var(--surface-alt);
  color: var(--text-primary);
}

.domain-shell {
  width: min(1180px, 100%);
  margin: 0 auto;
}

/* ── Page header ─────────────────────────────────────────────────────────── */

.page-header {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 20px;
}

.eyebrow {
  margin: 0 0 6px;
  color: #1f7a63;
  font-size: 0.76rem;
  font-weight: 850;
  text-transform: uppercase;
}

.page-header h1 {
  margin: 0;
  font-size: 2rem;
  font-weight: 850;
  line-height: 1.1;
}

.header-actions { display: flex; align-items: center; gap: 8px; }

/* ── Buttons ─────────────────────────────────────────────────────────────── */

.btn-primary,
.btn-ghost {
  min-height: 42px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 0 16px;
  border-radius: 8px;
  font: inherit;
  font-size: 0.88rem;
  font-weight: 800;
  cursor: pointer;
  transition: opacity 0.12s;
}

.btn-primary { border: 0; color: #fff; background: #f26e2e; }
.btn-ghost   { border: 1px solid var(--border-subtle); color: var(--text-secondary); background: var(--surface); }

.btn-primary:disabled,
.btn-ghost:disabled { opacity: 0.45; cursor: default; }

.btn-text {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  height: 30px;
  padding: 0 10px;
  border: 1px solid var(--border-subtle);
  border-radius: 6px;
  color: var(--text-secondary);
  background: var(--surface);
  font: inherit;
  font-size: 0.78rem;
  font-weight: 750;
  cursor: pointer;
  transition: border-color 0.12s, color 0.12s;
}

.btn-text:hover    { border-color: #9fc8ba; color: #1f5c4d; }
.btn-text:disabled { opacity: 0.5; cursor: default; }
.btn-text.danger:hover { border-color: rgba(198,40,40,.3); color: #b91c1c; }

.copy-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;
  flex-shrink: 0;
  padding: 0;
  border: 1px solid var(--border-subtle);
  border-radius: 6px;
  color: var(--text-secondary);
  background: var(--surface);
  cursor: pointer;
  transition: border-color 0.12s, color 0.12s;
}

.copy-btn.copied { border-color: #9fc8ba; color: #1f7a63; }

.icon-link-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;
  flex-shrink: 0;
  padding: 0;
  border: 1px solid var(--border-subtle);
  border-radius: 6px;
  color: var(--text-secondary);
  background: var(--surface);
  cursor: pointer;
}

.icon-link-btn:hover { border-color: #9fc8ba; color: #1f5c4d; }

/* ── Feedback bar ────────────────────────────────────────────────────────── */

.feedback-bar {
  display: flex;
  align-items: center;
  gap: 9px;
  margin-bottom: 14px;
  padding: 10px 14px;
  border: 1px solid rgba(31,122,99,.3);
  border-radius: 8px;
  color: #1f5c4d;
  background: #eef8f5;
  font-size: 0.88rem;
  font-weight: 700;
}

.reservation-notice {
  display: flex;
  align-items: flex-start;
  gap: 9px;
  margin-bottom: 14px;
  padding: 12px 14px;
  border: 1px solid rgba(180,131,9,.3);
  border-radius: 8px;
  color: #92610a;
  background: #fff8ea;
  font-size: 0.85rem;
  line-height: 1.5;
}

.feedback-bar.error { border-color: rgba(198,40,40,.2); color: #b91c1c; background: #fef2f2; }
.feedback-bar > span { flex: 1; }

.feedback-close {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  padding: 0;
  border: 0;
  border-radius: 4px;
  color: inherit;
  background: transparent;
  opacity: 0.6;
  cursor: pointer;
}

.feedback-close:hover { opacity: 1; }

/* ── Field ───────────────────────────────────────────────────────────────── */

.field { display: grid; gap: 7px; }

.field > span,
.label-text {
  display: block;
  color: var(--text-secondary);
  font-size: 0.72rem;
  font-weight: 750;
  text-transform: uppercase;
}

.field input,
.field select {
  width: 100%;
  min-height: 44px;
  padding: 0 12px;
  border: 1px solid var(--border-subtle);
  border-radius: 8px;
  color: var(--text-primary);
  background: var(--surface-alt);
  font: inherit;
  font-size: 0.92rem;
  outline: none;
}

.field input:focus,
.field select:focus { border-color: #9fc8ba; box-shadow: 0 0 0 3px rgba(31,122,99,.1); }

.inline-error { margin: 0; color: #b91c1c; font-size: 0.82rem; }

/* ── Setup hero ──────────────────────────────────────────────────────────── */

.hero-grid { display: grid; gap: 12px; }

.url-card {
  padding: 18px 20px;
  border: 1px solid var(--border-subtle);
  border-radius: 10px;
  background: var(--surface);
  box-shadow: 0 2px 8px rgba(23,35,31,.05);
}

.url-card-label {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 10px;
  color: var(--text-secondary);
  font-size: 0.76rem;
  font-weight: 750;
  text-transform: uppercase;
}

.url-card-value {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 10px;
}

.url-text {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  font-size: 1rem;
  font-weight: 700;
  color: var(--text-primary);
  text-overflow: ellipsis;
  white-space: nowrap;
}

.url-card-hint { margin: 0; color: var(--text-secondary); font-size: 0.82rem; }

.invite-card {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 18px 20px;
  border: 1px solid var(--border-subtle);
  border-radius: 10px;
  background: var(--surface);
  box-shadow: 0 2px 8px rgba(23,35,31,.05);
}

.invite-icon {
  width: 44px;
  height: 44px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 10px;
  color: #1f5c4d;
  background: #eef8f5;
}

.invite-body { flex: 1; min-width: 0; }
.invite-body strong { display: block; font-size: 1rem; font-weight: 850; margin-bottom: 4px; }
.invite-body p { margin: 0; color: var(--text-secondary); font-size: 0.88rem; line-height: 1.4; }

.invite-cta { flex-shrink: 0; }

/* ── Stepper ─────────────────────────────────────────────────────────────── */

.stepper-wrap {
  max-width: 580px;
  margin: 0 auto;
  display: grid;
  gap: 20px;
}

.step-progress {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0;
  padding: 4px 0 8px;
}

.step-progress-item {
  display: flex;
  align-items: center;
}

.step-dot {
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  border: 2px solid var(--border-subtle);
  background: var(--surface);
  color: var(--text-secondary);
  font-size: 0.78rem;
  font-weight: 800;
  transition: border-color 0.2s, background 0.2s, color 0.2s;
}

.step-dot.current { border-color: #f26e2e; color: #f26e2e; }
.step-dot.done    { border-color: #1f7a63; background: #1f7a63; color: #fff; }

.step-connector {
  width: 60px;
  height: 2px;
  background: var(--border-subtle);
  transition: background 0.2s;
}

.step-connector.done { background: #1f7a63; }

.step-body {
  background: var(--surface);
  border: 1px solid var(--border-subtle);
  border-radius: 10px;
  padding: 24px;
  display: grid;
  gap: 18px;
  box-shadow: 0 2px 12px rgba(23,35,31,.06);
}

.step-heading {
  margin: 0;
  font-size: 1.25rem;
  font-weight: 850;
  line-height: 1.2;
}

.step-sub {
  margin: -8px 0 0;
  color: var(--text-secondary);
  font-size: 0.9rem;
  line-height: 1.5;
}

.plan-card {
  padding: 18px 20px;
  border: 1px solid var(--border-subtle);
  border-radius: 10px;
  background: var(--surface-alt);
}

.plan-card-domain {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 6px;
  font-size: 0.95rem;
}

.plan-card-price {
  display: flex;
  align-items: baseline;
  gap: 4px;
  margin-bottom: 14px;
}

.plan-price {
  font-size: 1.8rem;
  font-weight: 850;
}

.plan-period {
  color: var(--text-secondary);
  font-size: 0.85rem;
}

.plan-features {
  display: grid;
  gap: 8px;
  margin: 0;
  padding: 0;
  list-style: none;
}

.plan-features li {
  display: flex;
  align-items: center;
  gap: 8px;
  color: var(--text-secondary);
  font-size: 0.88rem;
}

.plan-features li .v-icon {
  color: #1f7a63;
}

.step-nav {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-top: 4px;
  border-top: 1px solid var(--border-subtle);
}

.step-done { text-align: center; }
.step-done .step-heading { text-align: center; }
.step-done .step-sub { text-align: center; }

.done-icon {
  width: 60px;
  height: 60px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  margin: 0 auto;
}

.done-icon--success { background: #eef8f5; color: #1f7a63; }
.done-icon--pending { background: #fff7e6; color: #8a4b0f; }

.step-hint {
  margin: 0;
  color: var(--text-secondary);
  font-size: 0.84rem;
  text-align: center;
}

/* ── Method cards ────────────────────────────────────────────────────────── */

.method-list { display: grid; gap: 10px; }

.method-card {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 16px;
  border: 1.5px solid var(--border-subtle);
  border-radius: 10px;
  background: var(--surface);
  text-align: left;
  cursor: pointer;
  transition: border-color 0.14s, background 0.14s;
}

.method-card:hover { border-color: #9fc8ba; background: #f7fdfb; color: #17231f; }

.method-icon {
  width: 44px;
  height: 44px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 10px;
  color: #1f5c4d;
  background: #eef8f5;
}

.method-text { flex: 1; min-width: 0; }
.method-text strong { display: block; font-size: 0.95rem; font-weight: 850; color: var(--text-primary); margin-bottom: 3px; }
.method-text span   { display: block; font-size: 0.84rem; color: var(--text-secondary); }

.method-arrow { color: var(--text-secondary); flex-shrink: 0; }
.method-card:hover .method-arrow { color: #1f7a63; }
.method-card:hover .method-text strong { color: #17231f; }
.method-card:hover .method-text span { color: #6b5a4f; }

/* ── Domain search ───────────────────────────────────────────────────────── */

.search-input {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 0 10px;
  min-height: 44px;
  border: 1px solid var(--border-subtle);
  border-radius: 8px;
  color: var(--text-secondary);
  background: var(--surface-alt);
}

.search-input input { flex: 1; min-width: 0; border: 0; background: transparent; font: inherit; font-size: 0.92rem; outline: none; }

.search-input button {
  width: 34px;
  height: 34px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 0;
  border-radius: 6px;
  color: #fff;
  background: #17231f;
  cursor: pointer;
}

.search-input button:disabled { opacity: 0.5; cursor: default; }
.search-input:focus-within { border-color: #9fc8ba; box-shadow: 0 0 0 3px rgba(31,122,99,.1); }

.domain-results { display: grid; gap: 8px; }

.domain-result-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  min-height: 58px;
  padding: 10px 12px;
  border: 1px solid var(--border-subtle);
  border-radius: 8px;
  color: var(--text-primary);
  background: var(--surface);
  text-align: left;
  cursor: pointer;
  transition: border-color 0.14s, background 0.14s;
}

/* #fff7f2 is a fixed light peach, not theme-reactive — pin text to a fixed
   dark pair too, or it inherits var(--text-primary)/var(--text-secondary)
   and goes near-white on a light background in dark mode. */
.domain-result-row.selected,
.domain-result-row:not(:disabled):hover { border-color: rgba(242,110,46,.42); background: #fff7f2; color: #17231f; }

.domain-result-row.selected small,
.domain-result-row:not(:disabled):hover small { color: #6b5a4f; }

.domain-result-row.unavailable { opacity: 0.5; cursor: default; }
.domain-result-row.unavailable:hover { border-color: var(--border-subtle); background: var(--surface); }

.domain-result-row strong { display: block; font-size: 0.95rem; }
.domain-result-row small  { display: block; margin-top: 3px; color: var(--text-secondary); font-size: 0.74rem; }

.domain-price { color: #1f5c4d; font-size: 0.84rem; font-weight: 850; white-space: nowrap; }
.domain-taken { color: #b91c1c; font-size: 0.78rem; font-weight: 800; }

/* ── Registrant contact ──────────────────────────────────────────────────── */

.contact-section {
  padding: 16px;
  border: 1px solid var(--border-subtle);
  border-radius: 8px;
  background: var(--surface-alt);
}

.contact-title {
  margin: 0 0 12px;
  font-size: 0.88rem;
  font-weight: 800;
  color: var(--text-primary);
}

.contact-title span {
  margin-left: 8px;
  color: var(--text-secondary);
  font-size: 0.72rem;
  font-weight: 750;
  text-transform: uppercase;
}

.contact-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
}

.contact-full { grid-column: 1 / -1; }

.ca-legal-section {
  margin-top: 16px;
  padding-top: 16px;
  border-top: 1px solid var(--border-subtle);
}

/* ── Nameserver block ────────────────────────────────────────────────────── */

.ns-block {
  padding: 12px 14px;
  border: 1px solid var(--border-subtle);
  border-radius: 8px;
  background: var(--surface-alt);
  display: grid;
  gap: 6px;
}

.ns-list { display: grid; gap: 6px; }

.ns-row {
  display: flex;
  align-items: center;
  gap: 8px;
}

.ns-row code {
  flex: 1;
  font-family: 'Roboto Mono', ui-monospace, monospace;
  font-size: 0.86rem;
  color: var(--text-primary);
}

.ns-hint { margin: 0; color: var(--text-secondary); font-size: 0.84rem; }
.ns-hint a { color: #1f7a63; }

/* ── Info strip (domains view) ───────────────────────────────────────────── */

.info-strip {
  display: grid;
  grid-template-columns: 1fr 1fr auto auto;
  gap: 16px;
  align-items: start;
  padding: 16px 20px;
  margin-bottom: 14px;
  border: 1px solid var(--border-subtle);
  border-radius: 10px;
  background: var(--surface);
  box-shadow: 0 2px 8px rgba(23,35,31,.05);
}

.info-block { display: grid; gap: 8px; }

.url-row {
  display: flex;
  align-items: center;
  gap: 6px;
}

.url-text-sm {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  font-size: 0.84rem;
  font-weight: 700;
  color: var(--text-primary);
  text-overflow: ellipsis;
  white-space: nowrap;
}

.url-chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 5px 10px;
  border: 1px solid var(--border-subtle);
  border-radius: 8px;
  background: var(--surface-alt);
  color: var(--text-primary);
  font-size: 0.84rem;
  font-weight: 600;
  overflow: hidden;
}

.url-chip.active { border-color: rgba(31,122,99,.3); background: #eef8f5; color: #1f5c4d; }
.url-chip span { min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }

.pub-empty { color: var(--text-secondary); font-size: 0.84rem; }

.inline-input {
  min-height: 36px;
  padding: 0 10px;
  border: 1px solid var(--border-subtle);
  border-radius: 8px;
  color: var(--text-primary);
  background: var(--surface-alt);
  font: inherit;
  font-size: 0.88rem;
  outline: none;
  width: 100%;
}

.inline-input:focus { border-color: #9fc8ba; box-shadow: 0 0 0 3px rgba(31,122,99,.1); }

/* ── Domain cards ────────────────────────────────────────────────────────── */

.domains-list { display: grid; gap: 12px; }

.domain-card {
  padding: 16px;
  border: 1px solid var(--border-subtle);
  border-radius: 10px;
  background: var(--surface);
  box-shadow: 0 2px 8px rgba(23,35,31,.05);
}

.domain-card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 10px;
  margin-bottom: 14px;
}

.domain-card-title { display: flex; align-items: center; gap: 10px; }
.domain-name { font-size: 1.05rem; font-weight: 850; color: var(--text-primary); }
.domain-card-meta { display: flex; align-items: center; gap: 8px; }
.expiry-text { color: var(--text-secondary); font-size: 0.8rem; }

.primary-label {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 2px 8px;
  border-radius: 999px;
  color: #7c5c0a;
  background: #fef3c7;
  font-size: 0.68rem;
  font-weight: 800;
  text-transform: uppercase;
}

/* ── Badge ───────────────────────────────────────────────────────────────── */

.badge {
  display: inline-block;
  padding: 3px 8px;
  border-radius: 999px;
  font-size: 0.68rem;
  font-style: normal;
  font-weight: 800;
  text-transform: uppercase;
  white-space: nowrap;
}

.badge.active    { color: #1f5c4d; background: #eef8f5; }
.badge.pending   { color: #8a4b0f; background: #fff1dc; }
.badge.expired,
.badge.cancelled { color: #b91c1c; background: #fef2f2; }

/* ── DNS table ───────────────────────────────────────────────────────────── */

.dns-table { border: 1px solid var(--border-subtle); border-radius: 8px; overflow: hidden; }

.dns-row {
  display: grid;
  grid-template-columns: 64px 54px minmax(0,1fr) 80px;
  gap: 8px;
  padding: 9px 12px;
  border-top: 1px solid var(--border-subtle);
  color: var(--text-primary);
  font-size: 0.8rem;
  align-items: center;
}

.dns-row:first-child { border-top: 0; }

.dns-head {
  color: var(--text-secondary);
  background: var(--surface-alt);
  font-size: 0.7rem;
  font-weight: 800;
  text-transform: uppercase;
}

.dns-row strong {
  min-width: 0;
  overflow: hidden;
  font-weight: 700;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* ── Loading skeletons ───────────────────────────────────────────────────── */

.skel {
  border-radius: 8px;
  background: var(--surface-alt);
  animation: pulse 1.4s ease-in-out infinite;
}

.loading-header { margin-bottom: 16px; display: grid; gap: 10px; }
.skel-eyebrow   { width: 48px; height: 12px; }
.skel-h1        { width: 220px; height: 36px; }
.skel-hero      { height: 110px; border-radius: 10px; }
.skel-card      { border-radius: 10px; }

@keyframes pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.45; } }

/* ── Animations ──────────────────────────────────────────────────────────── */

.spin { animation: spin 0.7s linear infinite; }
@keyframes spin { to { transform: rotate(360deg); } }

.fade-enter-active, .fade-leave-active { transition: opacity 0.18s ease, transform 0.18s ease; }
.fade-enter-from,   .fade-leave-to     { opacity: 0; transform: translateY(-4px); }

.slide-down-enter-active { transition: opacity 0.2s ease, transform 0.2s ease; }
.slide-down-leave-active { transition: opacity 0.15s ease, transform 0.15s ease; }
.slide-down-enter-from,
.slide-down-leave-to     { opacity: 0; transform: translateY(-8px); }

/* ── DNS management ─────────────────────────────────────────────────────── */

.ns-loading {
  display: flex;
  align-items: center;
  gap: 7px;
  color: var(--text-secondary);
  font-size: 0.84rem;
}

.dns-section {
  border: 1px solid var(--border-subtle);
  border-radius: 8px;
  overflow: hidden;
}

.dns-toggle {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding: 10px 14px;
  border: 0;
  background: var(--surface-alt);
  color: var(--text-primary);
  font: inherit;
  font-size: 0.84rem;
  font-weight: 750;
  cursor: pointer;
  text-align: left;
}

.dns-toggle:hover { background: var(--surface-alt); }

.dns-count {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 20px;
  height: 20px;
  padding: 0 6px;
  border-radius: 999px;
  background: var(--border-subtle);
  color: var(--text-secondary);
  font-size: 0.72rem;
  font-weight: 800;
}

.toggle-arrow {
  margin-left: auto;
  color: var(--text-secondary);
  transition: transform 0.18s ease;
}

.toggle-arrow.open { transform: rotate(180deg); }

.dns-manage {
  border-top: 1px solid var(--border-subtle);
}

.dns-state-row {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 14px;
  font-size: 0.84rem;
  color: var(--text-secondary);
}

.dns-state-row.muted { color: var(--text-secondary); }

.dns-manage-table {
  overflow-x: auto;
}

.dns-manage-head,
.dns-manage-row,
.dns-edit-row {
  display: grid;
  grid-template-columns: 68px 1fr 1.6fr 70px 44px 72px;
  gap: 8px;
  padding: 8px 14px;
  align-items: center;
  border-bottom: 1px solid var(--surface-alt);
  font-size: 0.8rem;
}

.dns-manage-head {
  color: var(--text-secondary);
  background: var(--surface-alt);
  font-size: 0.7rem;
  font-weight: 800;
  text-transform: uppercase;
}

.dns-type-chip {
  display: inline-block;
  padding: 2px 7px;
  border-radius: 5px;
  background: var(--border-subtle);
  color: var(--text-primary);
  font-size: 0.72rem;
  font-weight: 800;
  text-transform: uppercase;
  white-space: nowrap;
}

.dns-cell { color: var(--text-primary); overflow: hidden; }
.dns-cell.ellipsis { text-overflow: ellipsis; white-space: nowrap; }
.dns-cell.muted { color: var(--text-secondary); }

.dns-row-actions {
  display: flex;
  align-items: center;
  gap: 4px;
  justify-content: flex-end;
}

.btn-icon-sm {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;
  padding: 0;
  border: 1px solid var(--border-subtle);
  border-radius: 6px;
  color: var(--text-secondary);
  background: var(--surface);
  cursor: pointer;
  transition: border-color 0.12s, color 0.12s;
}

.btn-icon-sm:hover       { border-color: #9fc8ba; color: #1f5c4d; }
.btn-icon-sm:disabled    { opacity: 0.4; cursor: default; }
.btn-icon-sm.danger:hover { border-color: rgba(198,40,40,.3); color: #b91c1c; }

.dns-select,
.dns-input {
  height: 32px;
  padding: 0 8px;
  border: 1px solid var(--border-subtle);
  border-radius: 6px;
  color: var(--text-primary);
  background: var(--surface);
  font: inherit;
  font-size: 0.82rem;
  outline: none;
  width: 100%;
}

.dns-select:focus,
.dns-input:focus { border-color: #9fc8ba; }

.proxied-toggle { display: flex; align-items: center; justify-content: center; }

.dns-add-form {
  padding: 14px;
  border-top: 1px solid var(--border-subtle);
  background: var(--surface-alt);
}

.dns-add-title {
  margin: 0 0 10px;
  font-size: 0.82rem;
  font-weight: 800;
  color: var(--text-primary);
}

.dns-add-grid {
  display: grid;
  grid-template-columns: 90px 1fr 2fr 90px 60px;
  gap: 8px;
  align-items: end;
  margin-bottom: 12px;
}

.dns-add-content { grid-column: span 1; }

.proxied-field { display: grid; gap: 7px; }
.proxied-toggle-wrap { display: flex; align-items: center; height: 36px; }

.dns-add-actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
}

.dns-add-btn {
  display: flex;
  align-items: center;
  gap: 6px;
  width: 100%;
  padding: 10px 14px;
  border: 0;
  border-top: 1px solid var(--border-subtle);
  background: transparent;
  color: #1f7a63;
  font: inherit;
  font-size: 0.82rem;
  font-weight: 800;
  cursor: pointer;
  transition: background 0.12s;
}

.dns-add-btn:hover { background: #eef8f5; }

/* ── Responsive ──────────────────────────────────────────────────────────── */

@media (max-width: 900px) {
  .info-strip { grid-template-columns: 1fr 1fr; }
}

@media (max-width: 640px) {
  .domain-page    { padding: 20px 12px 94px; }
  .page-header    { align-items: stretch; flex-direction: column; }
  .page-header h1 { font-size: 1.6rem; }
  .header-actions { flex-direction: column; }
  .btn-primary,
  .btn-ghost      { width: 100%; }
  .info-strip     { grid-template-columns: 1fr; }
  .invite-card    { flex-direction: column; align-items: flex-start; }
  .invite-cta     { width: 100%; }
  .contact-grid   { grid-template-columns: 1fr; }
  .domain-card-header { flex-direction: column; align-items: flex-start; }
  .dns-row { grid-template-columns: 52px 42px minmax(0,1fr) 64px; font-size: 0.74rem; }
  .step-connector { width: 32px; }
}
</style>
